import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { taskGroupContract } from "@common/contracts/task-group.contract";
import {
    TaskGroup,
    TaskGroupSchema,
    TaskGroupTaskCount,
} from "@common/types/taskGroup";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { invalidateAllTaskQueries } from "./taskQueryKeys";

export const taskGroupClient = getBackendAPIClient(taskGroupContract);

export const taskGroupQueryKeys = {
    all: () => ["task-group"] as const,
    workspace: (workspaceId: string) =>
        [...taskGroupQueryKeys.all(), "workspace", workspaceId] as const,
    organization: (organizationId: string) =>
        [...taskGroupQueryKeys.all(), "organization", organizationId] as const,
    list: (workspaceId: string) =>
        [...taskGroupQueryKeys.workspace(workspaceId), "list"] as const,
    organizationList: (organizationId: string) =>
        [...taskGroupQueryKeys.organization(organizationId), "list"] as const,
    counts: (workspaceId: string) =>
        [...taskGroupQueryKeys.workspace(workspaceId), "counts"] as const,
    organizationCounts: (organizationId: string) =>
        [...taskGroupQueryKeys.organization(organizationId), "counts"] as const,
};

export function useWorkspaceTaskGroups() {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId ? taskGroupQueryKeys.list(workspaceId) : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [];
            }

            const response =
                await taskGroupClient.listAvailableTaskGroupsForWorkspace({
                    params: { workspaceId },
                });

            return response.body.taskGroups.map((taskGroup) =>
                TaskGroupSchema.parse(taskGroup),
            );
        },
        enabled: !!workspaceId,
    });
}

export function useWorkspaceTaskGroupCounts() {
    const { workspaceId } = useScope();

    return useQuery({
        queryKey: workspaceId ? taskGroupQueryKeys.counts(workspaceId) : [],
        queryFn: async () => {
            if (!workspaceId) {
                return [] as TaskGroupTaskCount[];
            }

            const response =
                await taskGroupClient.countTasksByGroupForWorkspace({
                    params: { workspaceId },
                });

            return response.body.counts;
        },
        enabled: !!workspaceId,
    });
}

export function useCreateWorkspaceTaskGroupMutation() {
    const queryClient = useQueryClient();
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof taskGroupContract.createWorkspaceTaskGroup
            >["body"],
        ) => {
            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            const response = await taskGroupClient.createWorkspaceTaskGroup({
                params: { workspaceId },
                body,
            });

            return TaskGroupSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!workspaceId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.workspace(workspaceId),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}

export function useUpdateWorkspaceTaskGroupMutation(taskGroup?: TaskGroup) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: ServerInferRequest<
                typeof taskGroupContract.updateWorkspaceTaskGroup
            >["body"],
        ) => {
            if (!taskGroup?.workspaceId) {
                throw new Error("No task group selected");
            }

            const response = await taskGroupClient.updateWorkspaceTaskGroup({
                params: {
                    workspaceId: taskGroup.workspaceId,
                    groupId: taskGroup.id,
                },
                body,
            });

            return TaskGroupSchema.parse(response.body);
        },
        onSuccess: async () => {
            if (!taskGroup?.workspaceId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.workspace(
                        taskGroup.workspaceId,
                    ),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}

export function useDeleteWorkspaceTaskGroupMutation(taskGroup?: TaskGroup) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!taskGroup?.workspaceId) {
                throw new Error("No task group selected");
            }

            await taskGroupClient.deleteWorkspaceTaskGroup({
                params: {
                    workspaceId: taskGroup.workspaceId,
                    groupId: taskGroup.id,
                },
            });
        },
        onSuccess: async () => {
            if (!taskGroup?.workspaceId) {
                return;
            }

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: taskGroupQueryKeys.workspace(
                        taskGroup.workspaceId,
                    ),
                    exact: false,
                }),
                invalidateAllTaskQueries(queryClient),
            ]);
        },
    });
}
